import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { routes } from '@/lib/routes';
import { ArrowLeft } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { ChatWindow } from '@/tools/inner-team/components/chat/ChatWindow';
import { ChatInput } from '@/tools/inner-team/components/chat/ChatInput';
import { useChat } from '@/hooks/useChat';
import { useChatStore } from '@/store/useChatStore';
import { LoginPromptDialog } from '@/components/auth/LoginPromptDialog';
import { VoucherPromptDialog } from '@/components/auth/VoucherPromptDialog';

export function DiscoveryChat() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const sessions = useChatStore((s) => s.sessions);
  const session = sessions.find((s) => s.id === id);

  const setActiveSession = useChatStore((s) => s.setActiveSession);

  useEffect(() => {
    if (id) setActiveSession(id);
  }, [id, setActiveSession]);

  const { messages, isStreaming, sendMessage, loginRequired, dismissLoginPrompt, voucherRequired, dismissVoucherPrompt } = useChat(id ?? '', 'discovery');

  if (!session) {
    // Session not found — go back to list
    navigate(routes.innerTeam.discovery(), { replace: true });
    return null;
  }

  const displayTitle = session.title || 'Neue Erkundung';

  return (
    <AppShell>
      {/* Header with back button */}
      <div className="flex items-center gap-2 px-4 h-14 shrink-0 border-b border-border bg-background/80 backdrop-blur-sm">
        <button
          onClick={() => navigate(routes.innerTeam.discovery())}
          className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Zurück"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="flex-1 text-sm font-semibold truncate">{displayTitle}</h1>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <ChatWindow messages={messages} isStreaming={isStreaming} />
        <ChatInput onSend={sendMessage} disabled={isStreaming} />
      </div>
      <LoginPromptDialog open={loginRequired} onOpenChange={(open) => { if (!open) dismissLoginPrompt(); }} />
      <VoucherPromptDialog open={voucherRequired} onOpenChange={(open) => { if (!open) dismissVoucherPrompt(); }} />
    </AppShell>
  );
}
